<template>
  <section>
    <BaseIcon
      v-if="isLoading"
      icon="spinner"
      class="spinner"
    />
    <template v-else>
      <div v-html="conditionDisplayText(value)" />
      <RequisiteAdvanceRules
        v-if="value.subRules"
        :value="value.subRules"
        class="mt-2 ml-2 pl-4 border-l-2"
      />
    </template>
  </section>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'RequisiteAdvanceConditions',
  components: {
    RequisiteAdvanceRules: () =>
      import(/* webpackChunkName: 'RequisiteAdvanceRules' */ './RequisiteAdvanceRules.vue'),
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    level: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isLoading: true,
      relatedCourses: [],
      relatedDepartments: [],
    }
  },
  computed: {
    ...mapState('settings', ['school']),
  },
  async mounted() {
    this.isLoading = true
    const { courses = [] } = this.value
    const courseIds = courses
      .filter(({ courseId }) => courseId)
      .map(({ courseId }) => courseId)
    const departmentIds = courses
      .filter(({ departmentId }) => departmentId)
      .map(({ departmentId }) => departmentId)

    this.relatedCourses = await Promise.all(
      courseIds.map((courseId) =>
        this.$axios.$get(`/cm/${this.school}/courses/${encodeURIComponent(courseId)}`)
      )
    )

    this.relatedDepartments = await Promise.all(
      departmentIds.map((departmentId) =>
        this.$axios.$get(
          `/${this.school}/departments/${encodeURIComponent(departmentId)}`
        )
      )
    )
    this.isLoading = false
  },
  methods: {
    getRelatedCourseById(courseId) {
      return this.relatedCourses.find((course) => course._id === courseId) || {}
    },
    getRelatedDepartmentById(departmentId) {
      return (
        this.relatedDepartments.find(
          (department) => department._id === departmentId
        ) || {}
      )
    },
    courseLabel(course) {
      let label
      if (Object.keys(course).length === 1 && course.courseId) {
        // Direct course Select
        const courseData = this.getRelatedCourseById(course.courseId)
        label = `<a
            class="link"
            href="/courses/${encodeURIComponent(courseData._id)}"
            >${courseData._id &&
              `${courseData.subjectCode + courseData.courseNumber}</a> - ${
                courseData.name
              }`}`
      } else {
        // Dynamic Course Criteria
        const department =
          this.getRelatedDepartmentById(course.departmentId).displayName ||
          course.subjectCode
        if (course.type === 'simple') {
          label = `${department} ${course.number}`
        } else if (course.type === 'range') {
          label = `${department} courses with a course number range from ${
            course.min
          } to ${course.max}`
        } else if (course.type === 'wildcard') {
          // TODO add wildcard prefix with fixed suffix support
          label = `a ${department} course starting with ${course.number.split(
            '@'[0]
          )}`
        }
        if (course.withConditions && course.withConditions.length) {
          label += ` where ${course.withConditions
            .map((c) => `${c.condition} ${c.comparator} ${c.value}`)
            .join(', ')}`
        }
      }
      return label || 'Unknown Course'
    },
    grammarList(strings, conjunction) {
      if (strings.length === 0) return ''
      if (strings.length === 1) return strings[0]
      if (strings.length === 2)
        return `${strings[0]} ${conjunction} ${strings[1]}`

      // We use the Oxford comma
      const list = strings.slice(0, strings.length - 1).join(', ')
      return `${list}, ${conjunction} ${strings[strings.length - 1]}`
    },
    courseList(courses, conjunction) {
      const labels = courses.map((course) => this.courseLabel(course))
      return this.grammarList(labels, conjunction)
    },
    doPlural(word, number) {
      let singular = word
      if (singular === 'classes') singular = 'class'
      if (singular === 'credits') singular = 'credit'
      if (number === 1) return singular

      // -es rules
      if (singular.charAt(singular.length - 1) === 's') return `${singular}es`
      return `${singular}s`
    },
    conditionDisplayText(value) {
      const {
        condition,
        type,
        min,
        max,
        courses = [],
        blocks = [],
        text,
        subRules,
      } = value
      const orList = this.courseList(courses, 'or')
      const andList = this.courseList(courses, 'and')
      const blockList = this.grammarList(blocks, 'or')

      switch (condition) {
        case 'transfer':
        case 'passFail': {
          const suffix =
            condition === 'transfer' ? 'transfer' : 'take as pass/fail'
          if (max === 0) {
            if (!courses.length) return `No ${type} may be ${suffix}`
            return `${this.courseList(courses, 'AND')} may not be ${suffix}`
          } else {
            if (!courses.length)
              return `At most ${max} ${this.doPlural(
                type,
                max
              )} may be ${suffix}`
            return `At most ${max} ${this.doPlural(
              type,
              max
            )} from ${orList} may be ${suffix}`
          }
        }
        // I don't think we ever set MAX for either of these
        case 'residential':
        case 'lastResidential': {
          const last = condition === 'lastResidential' ? 'the last ' : ''
          return `At least ${last}${min} ${this.doPlural(
            type,
            min
          )} must be taken in residence`
        }
        case 'perDiscipline':
          if (max === undefined) {
            return `At least ${min} ${this.doPlural(
              type,
              min
            )} must be taken in each department`
          } else {
            if (min === undefined)
              return `At most ${max} ${this.doPlural(
                type,
                max
              )} must be taken in each department`
            return `Between ${min} and ${max} ${type} may be taken in each department`
          }

        case 'perTerm':
          if (!courses.length) {
            return `Only ${max} ${this.doPlural(
              type,
              max
            )} may be taken each term`
          } else {
            return `Only ${max} ${this.doPlural(
              type,
              max
            )} from ${andList} may be taken each term`
          }

        case 'scribe':
          return `Completed Scribe block ${text}`

        case 'subRules': {
          // TODO add either
          const actualRules = subRules.filter(
            (rule) => rule.condition !== 'freeformText'
          ).length
          if (max === undefined) {
            if (min === actualRules) return 'Complete all of the following:'
            if (min === 1) {
              if (actualRules === 2) return 'Complete either of the following:'
              return 'Complet any of the following:'
            }
            if (min >= 1) return `Complete ${min} of the following:`
            // Default to showing nothing
            return
          } else {
            if (min === undefined)
              return `Complete at most ${max} of the following`
            return `Complete between ${min} and ${max} of the following`
          }
        }
        case 'spread':
          if (max === undefined) {
            return `Courses must be taken in at least ${min} different departments`
          } else {
            if (min === undefined)
              return `Courses may be taken in at most ${max} different departments`
            return `Courses must be taken in between ${min} and ${max} different departments`
          }

        case 'grade':
          if (!courses.length) {
            return `Only grades above ${min} will count for credit`
          } else {
            return `Only grades above ${min} will count for credit in ${andList}`
          }

        case 'gpa':
          if (!courses.length) {
            return `Must have a GPA of ${min} or above`
          } else {
            return `Must have a GPA of ${min} or above in ${andList}`
          }

        case 'share':
          return `Credits used to satisfy this requirement may also be used to satisfy ${blockList} requirements`

        case 'complete':
          return `Requirement is complete`

        case 'incomplete':
          return `Requirement is NOT complete`

        // Often it's better to use text rules than noncourse rules
        case 'noncourse':
          if (min === undefined || min === 1) return `Completed ${text}`
          return `Complete ${text} ${min} times`

        case 'course':
          // This rule is used a lot, so it's important we use the most natural
          // language possible (that's why the logic is so complex)
          if (!courses.length) {
            if (type === 'credits') {
              // Taken 2 credits from Math 21 or Math 25
              if (max === undefined)
                return `At least ${min} ${this.doPlural(
                  'credits',
                  min
                )} must be taken`

              // Taken between 3 and 5 credits from Math 21 or Math 25
              return `Between ${min} and ${max} credits must be taken`
            }
            if (max !== undefined) {
              // Taken no classes
              if (max === 0) return `Taken no ${type}`
              // At most 4 classes will be counted
              if (min === undefined)
                return `At most ${max} ${this.doPlural(
                  type,
                  max
                )} will be counted`
              // Take exactly 8 classes
              if (min === max)
                return `Take exactly ${min} ${this.doPlural(type, max)}`
              // Take between 10 and 22 credits
              return `Take between ${min} and ${max} ${type}`
            }
          } else if (courses.length === 1) {
            const courseStr = this.courseLabel(courses[0])
            // Take Math 55
            if (min === 1) return `Take ${courseStr}`
            // Math 55 will not be counted
            if (max === 1) return `${courseStr} will not be counted`
            // Take Math 55 2 times (uncommon)
            if (min && type === 'classes')
              return `Take ${courseStr} ${min} ${this.doPlural('time', min)}`
            // Take Math 55 for 5 credits
            if (min && type === 'credits')
              return `Take ${courseStr} for ${min} credits`
          } else if (type === 'classes') {
            if (max === undefined) {
              // Take Math 55 or CS 121
              if (min === 1) return `Take ${orList}`
              // Take Math 55 and CS 121
              if (min === courses.length) return `Take ${andList}`
              // Take 2 classes from Math 21, Math 25, or Math 55
              return `Take ${min} classes from ${orList}`
            } else {
              // Math 21 and Math 25 will not be counted
              if (max === 0) return `${andList} will not be counted`
              // Take at most 1 class from Math 21, Math 25, or Math 55
              if (min === undefined)
                return `Take at most ${max} ${this.doPlural(
                  'classes',
                  max
                )} from ${orList}`
              // Take between 2 and 3 classes from Math 21, Math 25, or Math 55
              return `Take between ${min} and ${max} classes from ${orList}`
            }
          } else {
            // Taken 2 credits from Math 21 or Math 25
            if (max === undefined)
              return `Taken ${min} ${this.doPlural(
                'credits',
                min
              )} from ${orList}`

            // Take between 3 and 5 credits from Math 21 or Math 25
            return `Take between ${min} and ${max} credits from ${orList}`
          }
          // If we didn't match any of those rules, the rule is malformed
          return `Unable to understand the course rule`

        case 'credential': {
          const quantity = min || 1

          if (quantity === 1) {
            if (blockList.length === 1) return `Complete a ${blocks[0]}`
            return `Complete a ${blockList}`
          }
          return `Complete ${min} ${blockList} credentials`
        }
        // These are bound to look kinda ugly, and only exist so we can have
        // a complete Scribe integration
        case 'if':
          return `If ${text}:`
        case 'elseIf':
          return `Otherwise, if ${text}:`
        case 'else':
          return `Otherwise:`

        case 'freeformText':
          return text

        default:
          return 'Unknown requisite condition'
      }
    },
  },
}
</script>
